import { useState } from "react";

interface Product {
  id: string;
  status: string;
  category: string;
  subcategory: string;
  brand: string;
  title: string;
  description: string;
  price: string;
  main_image: string;
  images: string;
  affiliate_link: string;
  badge: string;
  created_at: string;
}

interface ProductCardProps {
  product: Product;
}

function getImageUrls(imagesString: string): string[] {
  if (!imagesString) return [];
  return imagesString.split(",").map((url) => url.trim()).filter(Boolean);
}

export default function ProductCard({ product }: ProductCardProps) {
  const [showGallery, setShowGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const allImages = [product.main_image, ...getImageUrls(product.images)].filter(Boolean);
  const hasMultipleImages = allImages.length > 1;

  return (
    <>
      <div className="group bg-oya-cream rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg border border-transparent hover:border-oya-gray-light">
        {/* Image */}
        <div
          className="relative aspect-square bg-white overflow-hidden cursor-pointer"
          onClick={() => {
            if (allImages.length > 0) {
              setCurrentImageIndex(0);
              setShowGallery(true);
            }
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" && allImages.length > 0) {
              setCurrentImageIndex(0);
              setShowGallery(true);
            }
          }}
        >
          {product.main_image ? (
            <img
              src={product.main_image}
              alt={product.title}
              className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-oya-cream">
              <i className="ri-image-line text-4xl text-oya-gray-medium" />
            </div>
          )}

          {product.badge && (
            <span className="absolute top-3 left-3 px-3 py-1 bg-oya-red text-white text-xs font-semibold rounded-full">
              {product.badge}
            </span>
          )}

          {hasMultipleImages && (
            <div className="absolute bottom-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-oya-black shadow-sm">
              <i className="ri-gallery-line text-sm" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-4 md:p-5">
          {product.brand && (
            <p className="text-xs text-oya-gray-medium font-medium uppercase tracking-wider mb-1">
              {product.brand}
            </p>
          )}
          <h3 className="text-sm md:text-base font-bold text-oya-black leading-snug line-clamp-2 mb-1">
            {product.title}
          </h3>
          <p className="text-xs md:text-sm text-oya-gray-medium line-clamp-2 mb-3">
            {product.description}
          </p>

          <div className="flex items-center justify-between gap-3">
            {product.price ? (
              <span className="text-base md:text-lg font-bold text-oya-red">
                {product.price}
              </span>
            ) : (
              <span />
            )}
            {product.affiliate_link ? (
              <a
                href={product.affiliate_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-oya-black text-white text-xs md:text-sm font-medium rounded-full hover:bg-oya-red transition-colors whitespace-nowrap"
              >
                Voir l'offre
                <i className="ri-external-link-line" />
              </a>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-oya-gray-light text-oya-gray-medium text-xs md:text-sm font-medium rounded-full whitespace-nowrap cursor-not-allowed">
                Non disponible
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox Gallery */}
      {showGallery && allImages.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-3 md:p-4 animate-fade-in"
          onClick={() => setShowGallery(false)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={() => setShowGallery(false)}
            className="absolute top-3 right-3 md:top-4 md:right-4 w-12 h-12 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors z-10"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-2xl" />
          </button>

          {allImages.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
              }}
              className="absolute left-2 md:left-8 w-12 h-12 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors z-10"
              aria-label="Image précédente"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
          )}

          <div
            className="max-w-4xl max-h-[85vh] w-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={allImages[currentImageIndex]}
              alt={`${product.title} - ${currentImageIndex + 1}`}
              className="max-w-full max-h-[80vh] md:max-h-[85vh] object-contain rounded-lg"
            />
          </div>

          {allImages.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
              }}
              className="absolute right-2 md:right-8 w-12 h-12 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors z-10"
              aria-label="Image suivante"
            >
              <i className="ri-arrow-right-s-line text-2xl" />
            </button>
          )}

          {allImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
              {allImages.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(idx);
                  }}
                  className={`h-2 rounded-full transition-all ${
                    idx === currentImageIndex ? "bg-white w-6" : "bg-white/40 w-2"
                  }`}
                  aria-label={`Image ${idx + 1}`}
                />
              ))}
            </div>
          )}

          {/* Swipe hint on mobile */}
          {allImages.length > 1 && (
            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/40 text-xs md:hidden">
              <span className="flex items-center gap-1">
                <i className="ri-arrow-left-right-line" />
                Glisser pour naviguer
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
}